import {
  Box,
  Button,
  Grid,
  Group,
  useMantineTheme,
  ScrollArea,
  Title,
  Text,
} from "@mantine/core";
import { useModals } from "@mantine/modals";
import { useCallback, useContext, useEffect, useState } from "react";
import Meeting from "./Meeting";
import { AppContext } from "src/api/AppContext";
import {
  CalendarView,
  EMPLOYEE_ROLE_OBJ,
  ROUTE_NAMES,
} from "src/utils/constants";
import ActionCard from "src/components/ActionCard";
import SegmentedControl from "src/components/SegmentedControl";
import {
  MdArrowBackIos,
  MdArrowForwardIos,
  MdOutlineDateRange,
} from "react-icons/md";
import CalendarComponent from "./CalendarComponent";
import { AiOutlineBars, AiOutlineDoubleRight } from "react-icons/ai";
import Navbar from "./Navbar";
import {
  GetMeetingsQuery,
  useGetCalendarFilterLocLazyQuery,
  useGetLocationDetailCalendarLazyQuery,
} from "src/generated/graphql";

import { formatDate } from "src/utils/dateHelper";
import useMeetingCalendar from "src/hooks/useMeetingCalendar";
import CalendarList from "./CalendarList";
import useWritePermission from "src/hooks/usePermission";

export type IFilter = {
  services: string[];
  instructors: string[];
  rooms: string[];
};

export type IMeeting = GetMeetingsQuery["meetingMany"];

export const defaultFilters = {
  services: [],
  instructors: [],
  rooms: [],
};

export default function CalendarScreen() {
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.calendar);
  const modals = useModals();
  const theme = useMantineTheme();
  const bg = theme.colors[theme.colorScheme][1];
  const {
    setCurrentPageTitle,
    currentLocation,
    currentEmployee,
    workspaceDimensions,
  } = useContext(AppContext);
  const {
    calendarInstance,
    calendarRef,
    dateRange,
    isListView,
    meetingDetails,
    meetingLoading,
    viewCalendar,
    handleNext,
    handlePrev,
    moveToday,
    setViewCalendar,
    setisListView,
    refetch,
  } = useMeetingCalendar();

  const [isNavbarOpen, setNavbarOpened] = useState(true);
  const [meetings, setMeetings] = useState<IMeeting>([]);
  const [filters, setFilters] = useState<IFilter>(defaultFilters);
  const [isFetchedFilter, setisFetchedFilter] = useState(!currentEmployee);

  const [getLocation, { data: locDetail }] =
    useGetLocationDetailCalendarLazyQuery();

  const [getCalendarFilter, { data: filtersData }] =
    useGetCalendarFilterLocLazyQuery({
      onCompleted() {
        resetFilters();
        setisFetchedFilter(true);
      },
      fetchPolicy: "network-only",
    });

  const resetFilters = () => {
    if (!filtersData?.calendarFilterLocation)
      return setMeetings(meetingDetails?.meetingMany || []);
    const { services, instructors, rooms } = filtersData.calendarFilterLocation;
    setFilters({
      services: services?.map((e) => e.id) || [],
      instructors: instructors?.map((e) => e.id) || [],
      rooms: rooms?.map((e) => e.id) || [],
    });
  };

  useEffect(() => {
    if (currentLocation?.id) {
      getLocation({ variables: { id: currentLocation?.id } });
      setFilters(defaultFilters);
    }
  }, [currentLocation, currentEmployee]);

  useEffect(() => {
    currentLocation &&
      currentEmployee &&
      getCalendarFilter({ variables: { locationId: currentLocation.id } });
  }, [meetingDetails, currentLocation, currentEmployee]);

  useEffect(() => {
    !currentEmployee && setMeetings(meetingDetails?.meetingMany || []);
  }, [meetingDetails]);

  useEffect(() => {
    setCurrentPageTitle(ROUTE_NAMES.CALENDER);
    return () => {
      setCurrentPageTitle(undefined);
    };
  }, []);

  useEffect(() => {
    //Filters
    if (!isFetchedFilter) return;
    let _meetings = meetingDetails?.meetingMany;
    if (
      _meetings?.length &&
      (filters.services.length ||
        filters.rooms.length ||
        filters.instructors.length)
    ) {
      if (filters.services?.length) {
        _meetings = _meetings.filter((meet) =>
          filters.services.includes(meet?.service?.id || "")
        );
      }
      if (filters.rooms?.length) {
        _meetings = _meetings.filter((meet) =>
          filters.rooms.includes(meet.room?.id || "")
        );
      }
      if (filters.instructors?.length) {
        _meetings = _meetings.filter((meet) =>
          filters.instructors.includes(meet.instructor?.id || "")
        );
      }
      setMeetings(_meetings);
    } else if (_meetings?.length) {
      setMeetings(_meetings);
    }
  }, [filters, meetingDetails, isFetchedFilter]);

  let modalId = "editMeeting";
  const handleModal = useCallback(
    (id = null, startDate = null) => {
      calendarInstance?.clearGridSelections();
      const currentDate = new Date(new Date().toUTCString());
      let withTime =
        new Date(new Date().setHours(0, 0, 0, 0)).valueOf() -
          new Date(startDate).valueOf() !==
        0;
      let sameDate =
        new Date(startDate).valueOf() ===
        new Date(new Date().setHours(0, 0, 0, 0)).valueOf();
      if (
        id ||
        (id === null && (startDate >= currentDate || (!withTime && sameDate)))
      ) {
        modals.openModal({
          closeOnClickOutside: false,
          title: id ? "Meeting Detail" : "Create Meeting",
          children: (
            <Meeting
              startDate={startDate}
              meetingId={id}
              handleEditModal={handleEditModal}
              refetch={refetch}
              meetings={meetings}
            />
          ),
        });
      } else {
        modals.openModal({
          closeOnClickOutside: false,
          title: `Create Meeting Warning`,
          children: (
            <Text color="red" align="center">
              You cannot create meeting in the past.
            </Text>
          ),
        });
      }
    },
    [calendarInstance, refetch]
  );

  const handleEditModal = useCallback(
    (id) => {
      modals.openModal({
        closeOnClickOutside: false,
        modalId,
        title: "Update Meeting",
        children: (
          <Meeting
            meetingId={id}
            refetch={refetch}
            modalId={modalId}
            isEdit={true}
            meetings={meetings}
          />
        ),
      });
    },
    [calendarInstance, refetch]
  );

  return (
    <>
      <ActionCard
        overflow="hidden"
        margin={0}
        titleContent={
          <Group sx={{ gap: 8 }}>
            <MdArrowBackIos onClick={handlePrev} />
            {formatDate(dateRange[0]).format1}
            {viewCalendar !== CalendarView.Day &&
              ` - ${formatDate(dateRange[1]).format1}`}
            <MdArrowForwardIos onClick={handleNext} />
          </Group>
        }
        title=""
        startIconButton={
          <Box>
            <SegmentedControl
              mr={5}
              data={[
                { value: "calendar", label: <MdOutlineDateRange /> },
                { value: "list", label: <AiOutlineBars /> },
              ]}
              onChange={(e) => setisListView(!isListView)}
            />
            <Button
              onClick={moveToday}
              radius="xl"
              size="sm"
              styles={{
                root: {
                  height: 30,
                  borderColor: theme.colors.primaryTextColor[3],
                  backgroundColor: theme.primaryColor[6],
                },
                inner: {
                  color: theme.colors.primaryTextColor[3],
                },
                label: {
                  color: theme.colors.primaryTextColor[3],
                },
              }}
            >
              Today
            </Button>
          </Box>
        }
        rightIconButton={
          <SegmentedControl
            data={Object.keys(CalendarView)}
            onChange={(e: CalendarView) =>
              setViewCalendar(e.toLocaleLowerCase() as CalendarView)
            }
          />
        }
      >
        <Grid sx={{ margin: 0 }}>
          {isNavbarOpen ? (
            <Navbar
              locDetail={locDetail?.locationOne}
              filters={filters}
              setFilters={setFilters}
              handleModal={() => {
                modals.openModal({
                  closeOnClickOutside: false,
                  title: "Create Meeting",
                  children: (
                    <Meeting
                      handleEditModal={handleEditModal}
                      refetch={refetch}
                      meetings={meetings}
                    />
                  ),
                });
              }}
              setNavbarOpened={setNavbarOpened}
              isAllowWrite={isAllowWrite}
            />
          ) : (
            <Box
              sx={{
                position: "absolute",
                zIndex: 99,
                top: 50,
                left: 0,
                background: bg,
                borderRadius: "0px 4px 4px 0px",
                borderWidth: "1px 1px 1px 0px",
                padding: "5px 8px",
                borderStyle: "solid",
                borderColor: "#DDDDDD",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={() => setNavbarOpened(true)}
            >
              <AiOutlineDoubleRight color="#4D4D4D" size={16} />
            </Box>
          )}

          <Grid.Col
            span={"auto"}
            sx={{
              padding: 0,
              ".toastui-calendar-day-view": {
                display: "flex",
                flexDirection: "column",
              },
              ".toastui-calendar-week-view": {
                display: "flex",
                flexDirection: "column",
              },
              ".toastui-calendar-time": {
                flex: 1,
              },
            }}
          >
            <Box
              style={{
                visibility: isListView ? "hidden" : "initial",
                height: isListView ? "0px" : "100%",
              }}
            >
              <CalendarComponent
                view={viewCalendar}
                ref={calendarRef}
                meetings={meetings}
                handleModal={handleModal}
                isAllowWrite={isAllowWrite}
                height={
                  workspaceDimensions && workspaceDimensions?.height > 100
                    ? `${workspaceDimensions?.height - 46}px`
                    : `70vh`
                }
              />
            </Box>
            {isListView && (
              <ScrollArea h={510}>
                <Box p={"0px 10px 20px 10px"}>
                  {meetings.length ? (
                    <CalendarList
                      dateRange={dateRange}
                      meetings={meetings}
                      handleModal={handleModal}
                      view={viewCalendar}
                    />
                  ) : (
                    <Title order={2} align="center" pt={"xl"}>
                      {!meetingLoading && "No Meeting available"}
                    </Title>
                  )}
                </Box>
              </ScrollArea>
            )}
          </Grid.Col>
        </Grid>
      </ActionCard>
    </>
  );
}
